import type { IChatKitWidgetNode } from '../interfaces/chatkit-widget.interface';
import { defaultLogger as logger } from '../telemetry/logger';

type Scope = Record<string, any>;

export function compileChatKitJsxTemplate(
  source: string,
  data: Record<string, any>
): IChatKitWidgetNode {
  const parser = new JsxParser(source, data);
  return parser.parse();
}

class JsxParser {
  private pos = 0;

  constructor(
    private readonly source: string,
    private readonly data: Record<string, any>,
    private readonly scope: Scope = {}
  ) {}

  parse(): IChatKitWidgetNode {
    this.skipWhitespace();
    const node = this.parseElement();
    this.skipWhitespace();
    return node;
  }

  private parseElement(): IChatKitWidgetNode {
    this.expect('<');
    if (this.peek() === '/') {
      throw new Error('Unexpected closing ChatKit JSX tag');
    }

    const type = this.readIdentifier();
    const props: Record<string, any> = {};

    while (!this.isEnd()) {
      this.skipWhitespace();
      if (this.startsWith('/>')) {
        this.pos += 2;
        return { type, ...props };
      }
      if (this.peek() === '>') {
        this.pos += 1;
        break;
      }
      const propName = this.readIdentifier();
      this.skipWhitespace();
      if (this.peek() !== '=') {
        props[propName] = true;
        continue;
      }
      this.pos += 1;
      this.skipWhitespace();
      props[propName] = this.readPropValue();
    }

    const children = this.readChildren(type);
    return children.length ? { type, ...props, children } : { type, ...props };
  }

  private readChildren(type: string): IChatKitWidgetNode[] {
    const children: IChatKitWidgetNode[] = [];
    while (!this.isEnd()) {
      if (this.startsWith(`</${type}`)) {
        this.pos += 2 + type.length;
        this.skipWhitespace();
        this.expect('>');
        return children;
      }

      if (this.peek() === '<') {
        children.push(this.parseElement());
        continue;
      }

      if (this.peek() === '{') {
        const expression = this.readBalanced('{', '}');
        children.push(...this.evaluateChildExpression(expression));
        continue;
      }

      const text = this.readText();
      if (text.trim()) {
        children.push({ type: 'Text', value: text.trim() });
      }
    }
    throw new Error(`Missing closing ChatKit JSX tag </${type}>`);
  }

  private readPropValue(): any {
    const char = this.peek();
    if (char === '"' || char === "'") return this.readQuotedString();
    if (char === '{') {
      const expression = this.readBalanced('{', '}');
      return evaluateValueExpression(expression, this.data, this.scope);
    }
    return this.readBareValue();
  }

  private evaluateChildExpression(expression: string): IChatKitWidgetNode[] {
    const trimmed = expression.trim();
    if (!trimmed) return [];

    const mapExpression = parseMapExpression(trimmed);
    if (mapExpression) {
      const items = resolvePath(
        { ...this.data, ...this.scope },
        mapExpression.collectionPath
      );
      if (!Array.isArray(items)) {
        logger.warn(
          'ChatKit JSX map source is not an array',
          mapExpression.collectionPath
        );
        return [];
      }
      return items.flatMap((item, index) => {
        const childScope = {
          ...this.scope,
          [mapExpression.itemName]: item,
          ...(mapExpression.indexName ? { [mapExpression.indexName]: index } : {}),
        };
        const body = stripWrappingParens(mapExpression.body);
        const parser = new JsxParser(body, this.data, childScope);
        return [parser.parse()];
      });
    }

    const value = evaluateValueExpression(trimmed, this.data, this.scope);
    if (Array.isArray(value)) {
      return value.filter(isWidgetNode);
    }
    if (isWidgetNode(value)) return [value];
    if (value == null || value === '') return [];
    return [{ type: 'Text', value: String(value) }];
  }

  private readIdentifier(): string {
    const start = this.pos;
    while (!this.isEnd() && /[A-Za-z0-9_$:.-]/.test(this.peek())) {
      this.pos += 1;
    }
    if (start === this.pos) {
      throw new Error(`Expected identifier at position ${this.pos}`);
    }
    return this.source.slice(start, this.pos);
  }

  private readBareValue(): string {
    const start = this.pos;
    while (!this.isEnd() && !/[\s>]/.test(this.peek())) {
      this.pos += 1;
    }
    return this.source.slice(start, this.pos);
  }

  private readQuotedString(): string {
    const quote = this.peek();
    this.pos += 1;
    let output = '';
    while (!this.isEnd()) {
      const char = this.peek();
      this.pos += 1;
      if (char === '\\') {
        output += this.peek();
        this.pos += 1;
        continue;
      }
      if (char === quote) return output;
      output += char;
    }
    throw new Error('Unterminated string in ChatKit JSX template');
  }

  private readBalanced(open: string, close: string): string {
    this.expect(open);
    let depth = 1;
    let quote: string | null = null;
    let output = '';

    while (!this.isEnd()) {
      const char = this.peek();
      this.pos += 1;

      if (quote) {
        output += char;
        if (char === '\\') {
          output += this.peek();
          this.pos += 1;
        } else if (char === quote) {
          quote = null;
        }
        continue;
      }

      if (char === '"' || char === "'" || char === '`') {
        quote = char;
        output += char;
        continue;
      }

      if (char === open) {
        depth += 1;
        output += char;
        continue;
      }

      if (char === close) {
        depth -= 1;
        if (depth === 0) return output;
        output += char;
        continue;
      }

      output += char;
    }

    throw new Error(`Unbalanced ${open}${close} in ChatKit JSX template`);
  }

  private readText(): string {
    const start = this.pos;
    while (!this.isEnd() && this.peek() !== '<' && this.peek() !== '{') {
      this.pos += 1;
    }
    return this.source.slice(start, this.pos);
  }

  private skipWhitespace(): void {
    while (!this.isEnd() && /\s/.test(this.peek())) this.pos += 1;
  }

  private startsWith(value: string): boolean {
    return this.source.startsWith(value, this.pos);
  }

  private expect(value: string): void {
    if (!this.startsWith(value)) {
      throw new Error(`Expected "${value}" at position ${this.pos}`);
    }
    this.pos += value.length;
  }

  private peek(): string {
    return this.source[this.pos];
  }

  private isEnd(): boolean {
    return this.pos >= this.source.length;
  }
}

interface MapExpression {
  collectionPath: string;
  itemName: string;
  indexName?: string;
  body: string;
}

function parseMapExpression(expression: string): MapExpression | null {
  const mapMatch = expression.match(
    /^([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*|\[\d+\]|\[['"][^'"]+['"]\])*)\.map\s*\(([\s\S]*)\)$/
  );
  if (!mapMatch) return null;

  const body = mapMatch[2].trim();
  const arrowIndex = body.indexOf('=>');
  if (arrowIndex < 0) return null;

  const rawParams = body.slice(0, arrowIndex).trim();
  const rawBody = body.slice(arrowIndex + 2).trim();
  const params = rawParams.replace(/^\(/, '').replace(/\)$/, '')
    .split(',')
    .map((param) => param.trim())
    .filter(Boolean);
  if (!params[0]) return null;

  return {
    collectionPath: mapMatch[1],
    itemName: params[0],
    indexName: params[1],
    body: rawBody,
  };
}

function stripWrappingParens(value: string): string {
  let output = value.trim();
  while (output.startsWith('(') && output.endsWith(')')) {
    const inner = output.slice(1, -1).trim();
    if (!isBalanced(inner)) break;
    output = inner;
  }
  return output;
}

function isBalanced(value: string): boolean {
  let parens = 0;
  let braces = 0;
  let quote: string | null = null;
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (quote) {
      if (char === '\\') i += 1;
      else if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'" || char === '`') {
      quote = char;
      continue;
    }
    if (char === '(') parens += 1;
    if (char === ')') parens -= 1;
    if (char === '{') braces += 1;
    if (char === '}') braces -= 1;
    if (parens < 0 || braces < 0) return false;
  }
  return parens === 0 && braces === 0 && quote == null;
}

function evaluateValueExpression(
  expression: string,
  data: Record<string, any>,
  scope: Scope
): any {
  const parser = new ValueExpressionParser(expression, { ...data, ...scope });
  return parser.parse();
}

class ValueExpressionParser {
  private pos = 0;

  constructor(
    private readonly source: string,
    private readonly scope: Scope
  ) {}

  parse(): any {
    this.skipWhitespace();
    const value = this.parseValue();
    this.skipWhitespace();
    return value;
  }

  private parseValue(): any {
    this.skipWhitespace();
    const char = this.peek();
    if (char === '"' || char === "'") return this.parseString();
    if (char === '{') return this.parseObject();
    if (char === '[') return this.parseArray();
    if (char === '-' || /\d/.test(char)) return this.parseNumber();
    return this.parseIdentifierValue();
  }

  private parseObject(): Record<string, any> {
    const output: Record<string, any> = {};
    this.expect('{');
    while (!this.isEnd()) {
      this.skipWhitespace();
      if (this.peek() === '}') {
        this.pos += 1;
        return output;
      }
      const key = this.parseKey();
      this.skipWhitespace();
      if (this.peek() === ':') {
        this.pos += 1;
        output[key] = this.parseValue();
      } else {
        output[key] = resolvePath(this.scope, key);
      }
      this.skipWhitespace();
      if (this.peek() === ',') this.pos += 1;
    }
    throw new Error('Unterminated object expression in ChatKit JSX template');
  }

  private parseArray(): any[] {
    const output: any[] = [];
    this.expect('[');
    while (!this.isEnd()) {
      this.skipWhitespace();
      if (this.peek() === ']') {
        this.pos += 1;
        return output;
      }
      output.push(this.parseValue());
      this.skipWhitespace();
      if (this.peek() === ',') this.pos += 1;
    }
    throw new Error('Unterminated array expression in ChatKit JSX template');
  }

  private parseKey(): string {
    this.skipWhitespace();
    const char = this.peek();
    if (char === '"' || char === "'") return this.parseString();
    return this.readIdentifier();
  }

  private parseIdentifierValue(): any {
    const path = this.readPath();
    if (path === 'true') return true;
    if (path === 'false') return false;
    if (path === 'null') return null;
    if (path === 'undefined') return undefined;
    return resolvePath(this.scope, path);
  }

  private parseNumber(): number {
    const start = this.pos;
    if (this.peek() === '-') this.pos += 1;
    while (!this.isEnd() && /\d/.test(this.peek())) this.pos += 1;
    if (this.peek() === '.') {
      this.pos += 1;
      while (!this.isEnd() && /\d/.test(this.peek())) this.pos += 1;
    }
    return Number(this.source.slice(start, this.pos));
  }

  private parseString(): string {
    const quote = this.peek();
    this.pos += 1;
    let output = '';
    while (!this.isEnd()) {
      const char = this.peek();
      this.pos += 1;
      if (char === '\\') {
        output += this.peek();
        this.pos += 1;
        continue;
      }
      if (char === quote) return output;
      output += char;
    }
    throw new Error('Unterminated string expression in ChatKit JSX template');
  }

  private readPath(): string {
    const start = this.pos;
    while (!this.isEnd() && /[A-Za-z0-9_$.[\]'"]/.test(this.peek())) {
      this.pos += 1;
    }
    if (start === this.pos) {
      throw new Error(`Expected value expression at position ${this.pos}`);
    }
    return this.source.slice(start, this.pos);
  }

  private readIdentifier(): string {
    const start = this.pos;
    while (!this.isEnd() && /[A-Za-z0-9_$-]/.test(this.peek())) {
      this.pos += 1;
    }
    if (start === this.pos) {
      throw new Error(`Expected object key at position ${this.pos}`);
    }
    return this.source.slice(start, this.pos);
  }

  private skipWhitespace(): void {
    while (!this.isEnd() && /\s/.test(this.peek())) this.pos += 1;
  }

  private expect(value: string): void {
    if (!this.source.startsWith(value, this.pos)) {
      throw new Error(`Expected "${value}" at position ${this.pos}`);
    }
    this.pos += value.length;
  }

  private peek(): string {
    return this.source[this.pos];
  }

  private isEnd(): boolean {
    return this.pos >= this.source.length;
  }
}

function resolvePath(scope: Scope, path: string): any {
  const normalized = path.replace(/\[(\d+)\]/g, '.$1')
    .replace(/\[['"]([^'"]+)['"]\]/g, '.$1');
  return normalized.split('.').reduce((current, part) => {
    if (current == null || part === '') return undefined;
    return current[part];
  }, scope as any);
}

function isWidgetNode(value: any): value is IChatKitWidgetNode {
  return !!value && typeof value === 'object' && typeof value.type === 'string';
}
